import { TITLE_SITE } from '@/config.js';
import { deepEquals } from '@dsee/util/objects.js';
import { escapeRegexp } from '@dsee/util/search.js';
import kindOf from 'kind-of';
import Vue from 'vue';


/**
 * Экранирование html спец. символов
 */
export function escapeHtml(source) {
    if (typeof (source) !== 'string')
        throw new TypeError('Parameter is not a string');

    const length = source.length;
    let result = '';

    for (let i = 0; i < length; i++) {
        const c = source.charAt(i);
        switch (c) {
            case '&':
                result += '&amp;';
                break;

            case '"':
                result += '&quot;';
                break;

            case '\'':
                result += '&#39;';
                break;

            case '<':
                result += '&lt;';
                break;

            case '>':
                result += '&gt;';
                break;

            default:
                result += c;
                break;
        }
    }

    return result;
}


/**
 * Преобразование CR LF в теги <br />
 */
export function nl2br(source) {
    if (typeof (source) !== 'string')
        throw new TypeError('Parameter is not a string');

    const length = source.length;
    let result = '';

    for (let i = 0; i < length; i++) {
        const c = source.charAt(i);
        switch (c) {
            case '\r':
                if (i + 1 < length && source.charAt(i + 1) === '\n')
                    i++;

            case '\n': // eslint-disable-line no-fallthrough
                result += '<br />';
                break;

            default:
                result += c;
                break;
        }
    }

    return result;
}


/**
 * Закодировать в base64 строку
 * @param {*} source объект кодирования в Base64
 */
export function encodeBase64(source) {
    return btoa(unescape(encodeURIComponent(JSON.stringify(source))));
}


/**
 * Декодировать base64 строку
 * @param {*} source объект декодирования в Base64
 */
export function decodeBase64(source) {
    return JSON.parse(decodeURIComponent(escape(atob(source))));
}


/**
 * Кодировать json строку
 * @param {*} source объект кодирования в json
 */
export function encodeJson(source) {
    return JSON.stringify(source);
}


/**
 * Декодировать json строку
 * @param {*} source объект декодирования в json
 */
export function decodeJson(source) {
    return JSON.parse(source);
}

/**
 * Создать геттер значений
 *
 * @param {*} value параметр - функция, или имя свойства, которое нужно вычитывать из объекта
 */
function toFunction(value) {
    if (typeof (value) == 'function')
        return value;

    if (typeof (value) == 'string')
        return (item) => item[value];

    throw new TypeError('Parameter is neither a mapping function, nor an attribute name');
}


const IDENTITY = it => it;


/**
 * Создать функцию поиска по указанному шаблону поиска
 * @param {*} source шаблон поиска
 */
export function foundByPattern(source) {
    if (source == null || source === '')
        return IDENTITY;

    if (typeof (source) !== 'string')
        throw new TypeError('Parameter is not a string');

    const parts = source.trim()
                        .split(/\s+/)
                        .filter(it => !!it);

    if (parts.length === 0)
        return IDENTITY;

    const regexp = new RegExp(parts.map(escapeRegexp).join('|'), 'ig');

    return (value) => {
        return value.replace(regexp, (match) => {
            return `<span class="found">${ match }</span>`;
        });
    };
}


/**
 * Извлечь домен из почтового адреса
 */
export function getEmailDomain(source) {
    if (typeof (source) !== 'string')
        throw new TypeError('Parameter #1:source is not a string');

    return source.toLowerCase().substring(source.lastIndexOf('@') + 1);
}


/**
 * Проверить равнозначность
 */
export function equals(a, b) {
    if (Object.is(a, b))
        return true;

    const atype = kindOf(a);

    if (atype !== kindOf(b))
        return false;

    switch (atype) {
        case 'array':
        case 'arguments':
            return arrayEquals(a, b);

        case 'object':
            return objectEquals(a, b);

        case 'map':
        case 'weakmap':
            return mapEquals(a, b);

        case 'set':
        case 'weakset':
            return setEquals(a, b);
    }

    return false;
}

function arrayEquals(a, b) {
    const length = a.length;

    if (length !== b.length)
        return false;

    for (let i = 0; i < length; i++) {
        if (!equals(a[i], b[i]))
            return false;
    }
    return true;
}

function objectEquals(a, b) {
    const keys = Object.keys(a);

    if (!setEquals(
        new Set(keys),
        new Set(Object.keys(b)),
    )) {
        return false;
    }
    for (const k of keys) {
        if (!equals(a[k], b[k]))
            return false;
    }
    return true;
}

function mapEquals(a, b) {
    if (!setEquals(
        new Set(a.keys()),
        new Set(b.keys()),
    )) {
        return false;
    }

    for (const k of a.keys()) {
        if (!equals(a.get(k), b.get(k)))
            return false;
    }
    return true;
}

function setEquals(a, b) {
    return (a.size === b.size) &&
        isPartOf(a, b) &&
        isPartOf(b, a);
}

function isPartOf(part, whole) {
    for (const it of part) {
        if (!whole.has(it))
            return false;
    }
    return true;
}

/**
 * Убрать html теги из текста
 */

export function html2text(text) {
    if (typeof text !== 'string')
        throw new TypeError('Parameter #1: text is not a string');

    let result = text;

    // удаляем комментарии
    result = result.replaceAll(/<!--[\s\S]*?-->/g, '');

    // удаляем скрипты
    result = result.replaceAll(/<script[\s\S]*?>[\s\S]*?<\/script>/g, '');

    // удаляем стили
    result = result.replaceAll(/<style[\s\S]*?>[\s\S]*?<\/style>/g, '');

    // удаляем теги br и hr
    result = result.replaceAll(/<br[\s\S]*?>/g, '\n');
    result = result.replaceAll(/<hr[\s\S]*?>/g, '\n');

    // заменяем блочные теги на символ \n
    result = result.replaceAll(/<(?:div|h\d+|p|pre|table|tr|li|ol|ul|dd|dl|dt|blockquote)[\s\S]*?>/g, ' ');

    // удалям ячейки таблиц
    result = result.replaceAll(/<t[dh][\s\S]*?>[\s\S]*?<\/t[dh]>/g, '');

    // удаляем все оставшиеся теги
    result = result.replaceAll(/<\/?[a-z][^>]*>/g, '');

    // удаляем символы quot, amp, lt, gt, nbsp, iexcl, cent, pound, yen, euro, copy, reg
    result = result.replaceAll(/&(quot|amp|lt|gt|nbsp|iexcl|cent|pound|yen|euro|copy|reg);/g, ' ');

    return result.trimStart().trimEnd();
}

/**
 * Привести строку к нижнему регистру ТОЛЬКО если это не акроним
 */
export function nameToLowerCase(text) {
    if (typeof (text) !== 'string')
        throw new TypeError('Parameter #1:text is not a string');

    if (isAcronym(text))
        return text;

    return text.toLowerCase();
}

function isAcronym(text) {
    for (let i = 0; i < text.length; i++) {
        const character = text.charAt(i);

        if (character === character.toLowerCase()) {
            return false;
        }
    }

    return true;
}


/**
 * Проверяет, является ли текущее разрешение экрана устройства мобильным.
 */
export function isMobile() {
    return matchMedia('(max-device-width: 1023px)').matches;
}


/**
 * Вычисляет сокращение на основе значения count.
 */
export function countReduction(count) {
    if (typeof (count) !== 'number')
        throw new TypeError('Parameter #1:count is not a number');

    if (count >= 1000000)
        return Math.round(count / 1000000) + ' млн.';

    if (count > 99999)
        return Math.round(count / 1000) + ' тыс.';

    return count;
}


/**
 *  Обновляет состояние компонента значениями из заданного объекта.
 *  @param {Object} oldState - Объект, содержащий старые значения состояния.
 *  @param {Object} newState - Объект, содержащий новые значения состояния.
 *  @param {Array} keyDate - Ключ, содержащий дату
 */
export function updateState(oldState, newState, keyDate = []) {
    if (typeof (oldState) !== 'object')
        throw new TypeError('Parameter #1:oldState is not an object');

    for (const key in oldState) {
        if (keyDate.includes(key) && newState[key] != null) {
            Vue.set(oldState, key, new Date(newState[key]));
            continue;
        }

        if (newState[key] != null)
            Vue.set(oldState, key, newState[key]);
    }
}

/**
 *  Обновляет состояние объекта компонента из заданного объекта (безразличен к отсутствующим ключам)
 *  @param {Object} oldItem - Объект, содержащий старые значения состояния.
 *  @param {Object} newItem - Объект, содержащий новые значения состояния.
 *  @param {Array} keyDate - Ключ, содержащий дату
 */
export function updateItemState(oldItem, newItem, keyDate = []) {
    if (typeof (oldItem) !== 'object')
        throw new TypeError('Parameter #1:oldItem is not an object');


    for (const [ key, value ] of Object.entries(oldItem)) {
        if (keyDate.includes(key)) {
            Vue.set(oldItem, key, new Date(newItem[key]));
            continue;
        }

        Vue.set(newItem, key, value);
    }
}


/**
 *  Получить последнее слово
 */
export function getLastWord(text) {
    if (typeof (text) !== 'string')
        throw new TypeError('Parameter #1:text is not a string');

    const lastSpace = text.lastIndexOf(' ');
    if (lastSpace === -1)
        return text;

    return text.slice(lastSpace + 1);
}


/**
 *  Удалить последнее слово
 */
export function removeLastWord(text) {
    if (typeof (text) !== 'string')
        throw new TypeError('Parameter #1:text is not a string');

    const lastSpace = text.lastIndexOf(' ');
    if (lastSpace === -1)
        return '';

    return text.slice(0, lastSpace);
}


/**
 * Генерирует массив контекстных предложений вокруг заданного слова.
 *
 * @param {string} text - Входной текст.
 * @param {string} word - Слово, которое нужно найти в тексте.
 * @param {number} [contextLength=60] - Длина контекста вокруг слова (по умолчанию: 60).
 * @returns {Object} {count: number, sentences: string[]} - Кол-во совпадений и массив контекстных предложений.
 */
export function sentencesAroundWord(text, word, contextLength = 60) {
    if (typeof (text) !== 'string')
        throw new TypeError('Parameter #1:text is not a string');
    if (typeof (word) !== 'string')
        throw new TypeError('Parameter #2:word is not a string');
    if (typeof (contextLength) !== 'number')
        throw new TypeError('Parameter #3:contextLength is not a number');

    const result = {
        count: 0,
        sentences: [],
    };

    if (!text.length || !word.length)
        return result;

    const indexes = findWordIndexes(text, word);

    // убрать значения находящиеся внутри одной длины контекста
    const filterIndexes = [];
    let endContext = 0;
    for (const index of indexes) {
        if (index < endContext)
            continue;

        filterIndexes.push(index);
        endContext = index + contextLength + word.length;
    }

    // Извлечь контекст для отфильтрованных совпадений
    const maxRetract = 10;
    for (const index of filterIndexes) {
        let start = Math.max(0, index - contextLength);
        if (start > 0) {
            const min = start - maxRetract;
            const max = start;
            const value = text.lastIndexOf(' ', start) + 1;
            start = limitValue(value, min, max);
        }

        let end = Math.min(index + word.length + contextLength, text.length);
        if (end < text.length) {
            const min = end;
            const max = end + maxRetract;
            const value = text.indexOf(' ', end);
            end = limitValue(value, min, max);
        }
        if (end === -1)
            end = text.length;

        let contextSentence = text.substring(start, end);

        if (start > 0)
            contextSentence = '…' + contextSentence;
        if (end < text.length)
            contextSentence = contextSentence + '…';

        result.sentences.push(contextSentence);
    }
    result.count = indexes.length;

    return result;
}


/**
 * Индексы всех совпадений в строке
 * @param {string} text - исходная строка
 * @param {string} word - искомое слово
 */

export function findWordIndexes(text, word) {
    if (typeof (text) !== 'string')
        throw new TypeError('Parameter #1:text is not a string');
    if (typeof (word) !== 'string')
        throw new TypeError('Parameter #2:word is not a string');

    if (!text.length || !word.length)
        return [];

    const parts = word.trim()
                      .split(/\s+/)
                      .filter(it => !!it);

    if (!parts.length)
        return [];

    const regexp = new RegExp(parts.map(escapeRegexp).join('|'), 'ig');
    const indexes = [];

    let match;

    while ((match = regexp.exec(text)) !== null) {
        indexes.push(match.index);
    }

    return indexes;
}


/**
 *  Получить случайное число в заданном диапазоне
 */
export function randomInRange(min, max) {
    if (typeof (min) !== 'number')
        throw new TypeError('Parameter #1:min is not a number');
    if (typeof (max) !== 'number')
        throw new TypeError('Parameter #2:max is not a number');

    return Math.random() * (max - min) + min;
}


/**
 * Ограничить максимальное и минимальное значение
 * @param {number} value - Значение, которое нужно ограничить
 * @param {number} min - Минимальное значение
 * @param {number} max - Максимальное значение
 * @returns {number} - Ограниченное значение
 */
export function limitValue(value, min, max) {
    if (typeof value !== 'number')
        throw new TypeError('Parameter #1:value is not a number');
    if (typeof min !== 'number')
        throw new TypeError('Parameter #2:min is not a number');
    if (typeof max !== 'number')
        throw new TypeError('Parameter #3:max is not a number');

    return Math.max(Math.min(value, max), min);
}


/**
 * Проверить, содержит ли строка заданное слово
 */
export function containsWord(text, findWord) {
    if (typeof (text) !== 'string')
        throw new TypeError('Parameter #1:text is not a string');
    if (typeof (findWord) !== 'string')
        throw new TypeError('Parameter #2:word is not a string');

    if (!text.length || !findWord.length)
        return false;

    const parts = findWord.trim()
                          .split(/\s+/)
                          .filter(it => !!it);

    if (!parts.length)
        return false;

    const regexp = new RegExp(parts.map(escapeRegexp).join('|'), 'ig');
    return (regexp.exec(text) !== null);
}


/**
 * Удалить дубликаты из массива
 */
export function removeDuplicates(value) {
    if (!Array.isArray(value))
        throw new TypeError('Parameter #1:value is not an array');

    if (!value.length || value.length === 1)
        return value;

    const result = value;

    for (let i = 0; i < value.length; i++) {
        for (let j = i + 1; j < value.length; j++) {
            if (deepEquals(value[i], value[j])) {
                result.splice(j, 1);
                j--;
            }
        }
    }

    return result;
}


/**
 * Получить инициалы
 */
export function getInitials(text, maxLength = 2) {
    if (typeof (text) !== 'string')
        throw new TypeError('Parameter #1:text is not a string');
    if (typeof (maxLength) !== 'number')
        throw new TypeError('Parameter #2:maxLength is not a number');

    if (!text.length)
        return '';

    const parts = text.trim()
                      .split(/\s+/)
                      .filter(it => !!it);

    if (!parts.length)
        return '';

    return parts.map(it => it[0]).join('')
                .slice(0, maxLength);
}


/**
 * Подставить title, description, дата публикации(опционально) в meta теги
 */
export function setDocumentMeta(title, description, date) {
    document.title = title ? `${ title } - ${ TITLE_SITE }` : TITLE_SITE;

    if (description) {
        document.querySelector('meta[name="description"]')
                .setAttribute('content', description);
    }

    if (date) {
        const dateElement = document.createElement('meta');
        dateElement.setAttribute('name', 'article:published_time');
        dateElement.setAttribute('content', date);

        document.head.append(dateElement);
    }
}
